import React, { useState, useEffect } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "react-toastify/dist/ReactToastify.css";
import OutsideClickHandler from "react-outside-click-handler";
import "../../Style/TicketsTable.css";

import { path_url } from "../../Config/config.js";

export default function TicketTable({ refresh }) {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [visibile, setVisible] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState({});
  const [filteredData, setFilteredData] = useState([]);

  const usercid = localStorage.getItem("userId");
  const token = localStorage.getItem("accessToken");

  const [columnsVisibility, setColumnsVisibility] = useState({
    id: true,
    title: true,
    description: true,
    status: true,
    priority: true,
    createdAt: true,
  });

  useEffect(() => {
    const initialColumns = columns.reduce(
      (acc, col) => ({ ...acc, [col.name]: true }),
      {},
    );
    setSelectedColumns(initialColumns);
  }, []);

  useEffect(() => {
    setFilteredData(
      tickets.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchText.toLowerCase()),
        ),
      ),
    );
  }, [searchText, tickets]);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${path_url}/ticket/user/${usercid}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (res.data.statusCodeValue === 200) {
        setTickets(res.data.body);
        setFilteredTickets(res.data.body);
      } else {
        toast.error(res.data.body || "Error fetching tickets.");
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      toast.error("Failed to fetch tickets.");
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [token, usercid, refresh]);

  useEffect(() => {
    const lowerSearch = searchText.toLowerCase();
    setFilteredTickets(
      tickets.filter((ticket) =>
        Object.values(ticket).some((val) =>
          String(val).toLowerCase().includes(lowerSearch),
        ),
      ),
    );
  }, [searchText, tickets]);

  const columns = [
    {
      name: <strong>#</strong>,
      selector: (row, index) => index + 1,
      omit: !columnsVisibility.id,
    },
    {
      name: <strong>Title</strong>,
      selector: (row) => row.title,
      omit: !columnsVisibility.title,
    },
    {
      name: <strong>Description</strong>,
      selector: (row) => row.description,
      omit: !columnsVisibility.description,
    },
    {
      name: <strong>Status</strong>,
      selector: (row) => row.status,
      omit: !columnsVisibility.status,
    },
    {
      name: <strong>Priority</strong>,
      selector: (row) => row.priority,
      omit: !columnsVisibility.priority,
    },
    {
      name: <strong>Date</strong>,
      selector: (row) => new Date(row.createdAt || Date.now()).toLocaleString(),
      omit: !columnsVisibility.createdAt,
    },
  ];

  const toggleColumn = (key) =>
    setColumnsVisibility({
      ...columnsVisibility,
      [key]: !columnsVisibility[key],
    });

  const exportCSV = () => {
    const csvHeader = Object.keys(columnsVisibility)
      .filter((key) => columnsVisibility[key])
      .join(",");

    const csvRows = filteredTickets.map((ticket, index) =>
      [
        index + 1,
        ticket.title,
        ticket.description,
        ticket.status,
        ticket.priority,
        new Date(ticket.createdAt).toLocaleString(),
      ]
        .filter((_, idx) => Object.values(columnsVisibility)[idx])
        .join(","),
    );

    const csvContent = [csvHeader, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "tickets.csv";
    link.click();
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Support Tickets", 14, 10);
    doc.autoTable({
      startY: 20,
      head: [
        Object.keys(columnsVisibility)
          .filter((key) => columnsVisibility[key])
          .map((key) => key.toUpperCase()),
      ],
      body: filteredTickets.map((ticket, index) =>
        [
          index + 1,
          ticket.title,
          ticket.description,
          ticket.status,
          ticket.priority,
          new Date(ticket.createdAt).toLocaleString(),
        ].filter((_, idx) => Object.values(columnsVisibility)[idx]),
      ),
    });
    doc.save("tickets.pdf");
  };

  const visibleColumns = columns.filter((col) => selectedColumns[col.name]);

  const copyToClipboard = () => {
    const textToCopy = filteredData
      .map((row) => visibleColumns.map((col) => col.selector(row)).join(", "))
      .join("\n");
    navigator.clipboard.writeText(textToCopy);
    alert("Table data copied to clipboard!");
  };

  const printTable = () => {
    const printContent = `
      <html><head><title>Print Tickets</title></head><body>
      <table border="1" style="width: 100%; border-collapse: collapse;">
      <tr>
        ${columns
          .filter((col) => !col.omit)
          .map((col) => `<th>${col.name}</th>`)
          .join("")}
      </tr>
      ${filteredTickets
        .map(
          (ticket, index) => `
        <tr>
          ${[
            index + 1,
            ticket.title,
            ticket.description,
            ticket.status,
            ticket.priority,
            new Date(ticket.createdAt).toLocaleString(),
          ]
            .filter((_, idx) => Object.values(columnsVisibility)[idx])
            .map((val) => `<td>${val}</td>`)
            .join("")}
        </tr>`,
        )
        .join("")}
      </table>
      </body></html>
    `;
    const newWin = window.open();
    newWin.document.write(printContent);
    newWin.document.close();
    newWin.print();
  };

  return (
    <div className="container mt-4 table-responsive">
      <div className="card ticket-table-card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong className="card-title">Support Ticket Table</strong>
        </div>

        {/* Responsive Toolbar */}
        <div className="row align-items-center mb-0 justify-content-between ticket-toolbar">
          <div className="col-12 col-md-6 mb-2 mb-md-0 d-flex flex-wrap">
            <div className="ticket-toolbar-actions">
              <label className="item" onClick={copyToClipboard}>
                Copy
              </label>
              <label className="item" onClick={exportCSV}>
                CSV
              </label>
              <label className="item" onClick={exportPDF}>
                PDF
              </label>
              <label className="item" onClick={printTable}>
                Print
              </label>
              <OutsideClickHandler onOutsideClick={() => setVisible(false)}>
                <div className="dropdown">
                  <button
                    className="btn text-white dropdown-toggle"
                    onClick={() => setVisible(!visibile)}
                  >
                    Column Visibility
                  </button>
                  {visibile && (
                    <div className="dropdown-menu show p-2">
                      {Object.keys(columnsVisibility).map((key) => (
                        <div key={key} className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={key}
                            checked={columnsVisibility[key]}
                            onChange={() => toggleColumn(key)}
                          />
                          <label htmlFor={key} className="form-check-label">
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </OutsideClickHandler>
            </div>
          </div>

          <div className="col-12 col-md-4 ticket-toolbar-search justify-content-end">
            <label className="p-0 m-0">Search:</label>
            <input
              type="text"
              className="form-control"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>

        <div className="table-responsive">
          <DataTable
            columns={columns}
            data={filteredTickets}
            pagination
            highlightOnHover
            striped
            responsive
            noHeader
          />
        </div>
      </div>
    </div>
  );
}